'use client';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import dynamic from 'next/dynamic';
import { Layers, Wind, CloudLightning, Cloud, Info, Loader2, Crosshair, Clock, CloudRain, Gauge, Play, Pause } from 'lucide-react';
import MapLegend from './MapLegend';
import { useAerodrome } from '@/context/AerodromeContext';
import { useSettings } from '@/context/SettingsContext';
import { getRegionalAerodromes, RegionalAerodrome } from '@/data/quebecAerodromes';
import { fetchSoaringForecast, getCachedOpenMeteoForecast } from '@/lib/openMeteoApi';
import { MOCK_AERODROMES } from '@/lib/mockWeatherData';
import type { DailySoaringForecast, HourlyCondition } from '@/lib/mockWeatherData';
import type { FrontData } from './LeafletMapView';
import { GEOMET_WMS_URL, RADAR_LAYERS } from './radarLayers';
import type { RadarLayerKey } from './radarLayers';

// Dynamically import the Leaflet map to avoid SSR issues
const LeafletMap = dynamic(() => import('./LeafletMapView'), { ssr: false, loading: () => (
  <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: '#060d1a' }}>
    <Loader2 size={28} className="animate-spin" style={{ color: '#00d4ff' }} />
  </div>
) });

interface AerodromeWeather {
  icao: string;
  verdict: DailySoaringForecast['verdict'];
  cbh: number;
  vz: number;
  loading: boolean;
  fetched: boolean;
}

export interface WeatherOverlayData {
  lat: number;
  lon: number;
  precipitation: number; // mm/h
  precipProbability: number; // %
  windSpeed: number;     // km/h
  windDir: number;       // degrees
  pressure: number;      // hPa
  stormRisk: number;     // indicative %
  hourly: Array<{
    hour: string;
    precipitation: number;
    precipProbability: number;
    windSpeed: number;
    windDir: number;
    pressure: number;
    stormRisk: number;
  }>;
}

export interface RadarFrame {
  time: number;
  isoTime?: string;
  layer: RadarLayerKey;
  label: string;
  source: 'geomet';
  kind?: 'observed' | 'forecast';
}

/** Generate synthetic front data around the active aerodrome */
function generateFronts(lat: number, lon: number, totalHours: number): FrontData[] {
  // Cold front: approaches from the west/northwest
  const coldFront: FrontData = {
    type: 'cold',
    points: [
      [lat + 4, lon - 6],
      [lat + 2, lon - 4.5],
      [lat, lon - 3],
      [lat - 2, lon - 1.5],
      [lat - 4, lon],
    ],
    impactHourIndex: Math.floor(totalHours * 0.75),
    label: 'Front Froid',
  };

  // Warm front: approaches from the southwest, impacts earlier
  const warmFront: FrontData = {
    type: 'warm',
    points: [
      [lat - 3, lon - 7],
      [lat - 1.5, lon - 5],
      [lat, lon - 3.5],
      [lat + 1.5, lon - 2],
      [lat + 3, lon - 0.5],
    ],
    impactHourIndex: Math.floor(totalHours * 0.45),
    label: 'Front Chaud',
  };

  return [warmFront, coldFront];
}

export default function MeteoMapContent() {
  const { selectedAerodrome: globalAerodrome, setSelectedAerodrome, hydrated } = useAerodrome();
  const { settings } = useSettings();

  const [activeOverlay, setActiveOverlay] = useState<'cbh' | 'wind' | 'storm' | 'radar' | 'pressure'>('cbh');
  const [showFronts, setShowFronts] = useState(false);
  const [radius, setRadius] = useState(150);
  const [dateStr, setDateStr] = useState('');
  const [weatherData, setWeatherData] = useState<AerodromeWeather[]>([]);
  const [weatherOverlay, setWeatherOverlay] = useState<WeatherOverlayData[]>([]);
  const [radarHost, setRadarHost] = useState('');
  const [radarFrames, setRadarFrames] = useState<RadarFrame[]>([]);
  const [activeRadarLayer, setActiveRadarLayer] = useState<RadarLayerKey>('rain');
  const [radarStatus, setRadarStatus] = useState('');
  const [overlayHourIndex, setOverlayHourIndex] = useState(0);
  const [overlayPlaying, setOverlayPlaying] = useState(false);
  const [overlayLoading, setOverlayLoading] = useState(false);
  const [globalLoading, setGlobalLoading] = useState(false);
  const [activeCeiling, setActiveCeiling] = useState(0);
  // Hourly ceiling data for the active aerodrome
  const [hourlyConditions, setHourlyConditions] = useState<HourlyCondition[]>([]);
  const [selectedHourIndex, setSelectedHourIndex] = useState<number>(-1); // -1 = use daily max

  // Compute regional aerodromes based on selected aerodrome location
  const regionalAerodromes = useMemo(
    () => getRegionalAerodromes(globalAerodrome.lat, globalAerodrome.lon),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [globalAerodrome.icao]
  );

  // Initialize weather data state for all regional aerodromes
  useEffect(() => {
    setWeatherData(
      regionalAerodromes.map((a) => ({
        icao: a.icao,
        verdict: 'marginal' as const,
        cbh: 0,
        vz: 0,
        loading: false,
        fetched: false,
      }))
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalAerodrome.lon > -30 ? 'france' : 'canada']);

  // Fetch soaring forecast for visible aerodromes
  const fetchingRef = useRef<Set<string>>(new Set());

  const fetchWeatherForAerodromes = useCallback(async (aerodromes: RegionalAerodrome[]) => {
    const toFetch = aerodromes.filter((a) => !fetchingRef.current.has(a.icao));
    if (toFetch.length === 0) return;

    setGlobalLoading(true);
    setWeatherData((prev) =>
      prev.map((w) =>
        toFetch.some((a) => a.icao === w.icao) ? { ...w, loading: true } : w
      )
    );

    for (let i = 0; i < toFetch.length; i++) {
      const ad = toFetch[i];
      if (fetchingRef.current.has(ad.icao)) continue;
      fetchingRef.current.add(ad.icao);

      if (i > 0) await new Promise<void>((r) => setTimeout(r, 250));

      // Find in MOCK_AERODROMES first, else use regional data
      const mockData = MOCK_AERODROMES.find((m) => m.icao === ad.icao);
      const aerodromeForFetch = mockData ?? {
        icao: ad.icao,
        name: ad.name,
        lat: ad.lat,
        lon: ad.lon,
        elevation: Math.round(ad.elevation * 0.3048),
        country: ad.lon < -30 ? 'CA' : 'FR',
      };

      try {
        const result = await fetchSoaringForecast(aerodromeForFetch);
        const { today } = result;
        setWeatherData((prev) =>
          prev.map((w) =>
            w.icao === ad.icao
              ? { ...w, verdict: today.verdict, cbh: today.maxCumulusBase, vz: today.maxVZ, loading: false, fetched: true }
              : w
          )
        );
        // Update ceiling and hourly data for the selected (active) aerodrome
        if (ad.icao === globalAerodrome.icao) {
          setActiveCeiling(today.maxCumulusBase);
          setHourlyConditions(today.hourly ?? []);
          setSelectedHourIndex(-1);
        }
      } catch {
        setWeatherData((prev) =>
          prev.map((w) =>
            w.icao === ad.icao ? { ...w, loading: false, fetched: true } : w
          )
        );
      } finally {
        fetchingRef.current.delete(ad.icao);
      }
    }
    setGlobalLoading(false);
  }, [globalAerodrome.icao]);

  const animatedOverlayActive = activeOverlay === 'radar' || activeOverlay === 'storm' || activeOverlay === 'wind' || activeOverlay === 'pressure';
  const overlayHours = activeOverlay === 'radar'
    ? radarFrames.map((frame) => frame.label)
    : animatedOverlayActive
      ? weatherOverlay[0]?.hourly?.map((h) => h.hour) ?? hourlyConditions.map((h) => h.hour)
      : hourlyConditions.map((h) => h.hour);
  const activeOverlayHour = overlayHours[overlayHourIndex] ?? '--:--';
  const radarCoverageAvailable =
    (globalAerodrome.country || '').toUpperCase() === 'CA' ||
    (globalAerodrome.country || '').toUpperCase() === 'US' ||
    (globalAerodrome.lat >= 15 && globalAerodrome.lat <= 85 && globalAerodrome.lon >= -170 && globalAerodrome.lon <= -40);
  const activeRadarConfig = RADAR_LAYERS[activeRadarLayer];

  const fetchRadarFrames = useCallback(async () => {
    setOverlayLoading(true);
    setRadarStatus('');
    try {
      if (!radarCoverageAvailable) {
        setRadarHost('');
        setRadarFrames([]);
        setRadarStatus('Radar ECCC indisponible ou hors couverture pour cette zone.');
        return;
      }

      const parseLayerTimes = async (layer: string) => {
        const response = await fetch(`${GEOMET_WMS_URL}?SERVICE=WMS&VERSION=1.3.0&REQUEST=GetCapabilities&layer=${encodeURIComponent(layer)}`);
        if (!response.ok) throw new Error(`GeoMet GetCapabilities ${response.status}`);
        const capabilities = await response.text();
        const layerStart = capabilities.indexOf(`<Name>${layer}</Name>`);
        const layerBlock = layerStart >= 0 ? capabilities.slice(layerStart, layerStart + 20000) : capabilities;
        const dimension =
          layerBlock.match(/<Dimension[^>]*name=["']time["'][^>]*>([\s\S]*?)<\/Dimension>/i)?.[1]?.trim() ??
          layerBlock.match(/<Extent[^>]*name=["']time["'][^>]*>([\s\S]*?)<\/Extent>/i)?.[1]?.trim() ??
          '';
        if (dimension.includes(',')) return dimension.split(',').map((v) => v.trim()).filter(Boolean);
        if (!dimension.includes('/')) return [];
        const [startRaw, endRaw, stepRaw] = dimension.split('/').map((v) => v.trim());
        const stepMinutes = Number.parseInt(stepRaw?.match(/PT(\d+)M/i)?.[1] ?? '6', 10);
        const start = Date.parse(startRaw);
        const end = Date.parse(endRaw);
        if (!Number.isFinite(start) || !Number.isFinite(end) || !Number.isFinite(stepMinutes)) return [];
        const values: string[] = [];
        for (let t = start; t <= end; t += stepMinutes * 60_000) {
          values.push(new Date(t).toISOString());
        }
        return values;
      };

      const now = Date.now();
      const threeHoursAgo = now - 3 * 60 * 60_000;
      const twelveHoursAhead = now + 12 * 60 * 60_000;
      const times = await parseLayerTimes(activeRadarConfig.layer);
      const frames = times
        .filter((isoTime) => {
          const t = Date.parse(isoTime);
          if (!Number.isFinite(t)) return false;
          if (activeRadarConfig.kind === 'forecast') return t >= now - 10 * 60_000 && t <= twelveHoursAhead;
          return t >= threeHoursAgo && t <= now + 10 * 60_000;
        })
        .map((isoTime) => {
          const t = Date.parse(isoTime);
          const timeLabel = new Date(isoTime).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' });
          const leadHours = Math.max(0, Math.round((t - now) / 3_600_000));
          return {
            time: Math.round(t / 1000),
            isoTime,
            layer: activeRadarLayer,
            label: activeRadarConfig.kind === 'forecast' ? `+${leadHours}h ${timeLabel}` : timeLabel,
            source: 'geomet' as const,
            kind: activeRadarConfig.kind,
          };
        })
        .filter((frame) => Number.isFinite(frame.time))
        .sort((a, b) => a.time - b.time);

      setRadarHost(GEOMET_WMS_URL);
      setRadarFrames(frames);
      if (frames.length > 0) {
        setOverlayHourIndex(activeRadarConfig.kind === 'forecast' ? 0 : frames.length - 1);
        setRadarStatus('Radar actif — aucune précipitation détectée sur la zone affichée.');
      } else {
        setRadarStatus('Radar ECCC indisponible ou hors couverture pour cette zone.');
      }
    } catch {
      setRadarHost('');
      setRadarFrames([]);
      setRadarStatus('Radar ECCC indisponible ou hors couverture pour cette zone.');
    } finally {
      setOverlayLoading(false);
    }
  }, [activeRadarConfig, activeRadarLayer, radarCoverageAvailable]);

  useEffect(() => {
    if (!overlayPlaying || !animatedOverlayActive || overlayHours.length <= 1) return;
    const id = window.setInterval(() => {
      setOverlayHourIndex((idx) => (idx + 1) % overlayHours.length);
    }, 900);
    return () => window.clearInterval(id);
  }, [overlayPlaying, animatedOverlayActive, overlayHours.length]);

  useEffect(() => {
    setOverlayHourIndex(0);
    setOverlayPlaying(false);
  }, [globalAerodrome.icao, activeOverlay]);

  useEffect(() => {
    if (activeOverlay !== 'radar') return;
    fetchRadarFrames();
  }, [activeOverlay, fetchRadarFrames]);

  // Fetch weather overlay grid from Open-Meteo
  const fetchWeatherOverlay = useCallback(async (lat: number, lon: number) => {
    setOverlayLoading(true);
    try {
      // Build a small grid of points around the selected aerodrome
      const gridPoints: WeatherOverlayData[] = [];
      const gridSize = 2;
      const step = 0.8;

      const promises = [];
      for (let dlat = -gridSize; dlat <= gridSize; dlat += 2) {
        for (let dlon = -gridSize; dlon <= gridSize; dlon += 2) {
          const glat = lat + dlat * step;
          const glon = lon + dlon * step;
          const overlayUrl =
            `https://api.open-meteo.com/v1/forecast?latitude=${glat.toFixed(3)}&longitude=${glon.toFixed(3)}&hourly=precipitation,precipitation_probability,showers,cape,wind_speed_10m,wind_direction_10m,surface_pressure&daily=sunrise,sunset&wind_speed_unit=kmh&timezone=auto&forecast_days=2`;
          promises.push(
            getCachedOpenMeteoForecast({ url: overlayUrl, cacheTtlMs: 10 * 60 * 1000 })
              .then(({ data }) => {
                if (data?.hourly?.time) {
                  const parseLocalMinutes = (iso: string | undefined, fallback: number) => {
                    const hhmm = iso?.split('T')[1]?.slice(0, 5);
                    if (!hhmm) return fallback;
                    const [hh, mm] = hhmm.split(':').map((v) => Number.parseInt(v, 10));
                    return Number.isFinite(hh) && Number.isFinite(mm) ? hh * 60 + mm : fallback;
                  };
                  const sunriseMinute = parseLocalMinutes(data.daily?.sunrise?.[0], 5 * 60);
                  const sunsetMinute = parseLocalMinutes(data.daily?.sunset?.[0], 22 * 60);
                  const frames = data.hourly.time
                    .map((time: string, idx: number) => {
                      const localTime = time.split('T')[1] ?? '00:00';
                      const hour = Number.parseInt(localTime.slice(0, 2), 10);
                      const minute = Number.parseInt(localTime.slice(3, 5), 10);
                      const minuteOfDay = (Number.isFinite(hour) ? hour : 0) * 60 + (Number.isFinite(minute) ? minute : 0);
                      const precipitation = (data.hourly.precipitation?.[idx] ?? 0) + (data.hourly.showers?.[idx] ?? 0);
                      const probability = data.hourly.precipitation_probability?.[idx] ?? 0;
                      const cape = data.hourly.cape?.[idx] ?? 0;
                      const stormRisk = Math.min(100, Math.round((cape / 1000) * 55 + probability * 0.25));
                      return {
                        hour: `${String(hour).padStart(2, '0')}:00`,
                        minuteOfDay,
                        precipitation: Math.round(precipitation * 10) / 10,
                        precipProbability: Math.round(probability),
                        windSpeed: Math.round(data.hourly.wind_speed_10m?.[idx] ?? 0),
                        windDir: Math.round(data.hourly.wind_direction_10m?.[idx] ?? 0),
                        pressure: Math.round(data.hourly.surface_pressure?.[idx] ?? 1013),
                        stormRisk,
                      };
                    })
                    .filter((frame: { minuteOfDay: number }) => frame.minuteOfDay >= sunriseMinute && frame.minuteOfDay <= sunsetMinute)
                    .map((frame: any) => {
                      const { minuteOfDay: _minuteOfDay, ...cleanFrame } = frame;
                      return cleanFrame;
                    });
                  const first = frames[0] ?? {
                    hour: '--:--',
                    precipitation: data.hourly.precipitation?.[0] ?? 0,
                    precipProbability: 0,
                    windSpeed: data.hourly.wind_speed_10m?.[0] ?? 0,
                    windDir: data.hourly.wind_direction_10m?.[0] ?? 0,
                    pressure: data.hourly.surface_pressure?.[0] ?? 1013,
                    stormRisk: 0,
                  };
                  gridPoints.push({
                    lat: glat,
                    lon: glon,
                    precipitation: first.precipitation,
                    precipProbability: first.precipProbability,
                    windSpeed: first.windSpeed,
                    windDir: first.windDir,
                    pressure: first.pressure,
                    stormRisk: first.stormRisk,
                    hourly: frames,
                  });
                }
              })
              .catch(() => {})
          );
        }
      }
      await Promise.all(promises);
      setWeatherOverlay(gridPoints);
      setOverlayHourIndex(0);
    } catch {
      // silently fail
    } finally {
      setOverlayLoading(false);
    }
  }, []);

  // Fetch data when aerodrome changes
  useEffect(() => {
    if (!hydrated) return;
    fetchingRef.current.clear();
    setHourlyConditions([]);
    setSelectedHourIndex(-1);
    const nearby = regionalAerodromes.slice(0, 12);
    fetchWeatherForAerodromes(nearby);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hydrated, globalAerodrome.icao]);

  useEffect(() => {
    if (activeOverlay !== 'wind' && activeOverlay !== 'storm' && activeOverlay !== 'pressure') return;
    fetchWeatherOverlay(globalAerodrome.lat, globalAerodrome.lon);
  }, [activeOverlay, fetchWeatherOverlay, globalAerodrome.lat, globalAerodrome.lon]);

  useEffect(() => {
    const d = new Date();
    setDateStr(
      `${d.getDate().toString().padStart(2, '0')}/${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getFullYear()}`
    );
  }, []);

  const fetchedAerodromes = weatherData.filter((a) => a.fetched);
  const favorableCount = fetchedAerodromes.filter((a) => a.verdict === 'favorable').length;
  const avgCBH =
    fetchedAerodromes.length > 0
      ? Math.round(fetchedAerodromes.reduce((s, a) => s + a.cbh, 0) / fetchedAerodromes.length)
      : 0;
  const stormCount = fetchedAerodromes.filter((a) => a.verdict === 'storm-risk').length;

  // Compute the ceiling to use for the glide circle
  const glideCircleCeiling = useMemo(() => {
    if (selectedHourIndex >= 0 && hourlyConditions[selectedHourIndex]) {
      return hourlyConditions[selectedHourIndex].cumulusBase ?? 0;
    }
    return activeCeiling;
  }, [selectedHourIndex, hourlyConditions, activeCeiling]);

  // Convective hours for the timeline selector
  const convectiveHours = useMemo(
    () => hourlyConditions.filter((h) => h.isConvective),
    [hourlyConditions]
  );

  // Wind at selected hour (for patatoïde deformation)
  const glideWindSpeed = useMemo(() => {
    if (selectedHourIndex >= 0 && hourlyConditions[selectedHourIndex]) {
      return hourlyConditions[selectedHourIndex].windSpeed ?? 0;
    }
    // average over convective hours
    if (convectiveHours.length > 0) {
      return Math.round(convectiveHours.reduce((s, h) => s + (h.windSpeed ?? 0), 0) / convectiveHours.length);
    }
    return 0;
  }, [selectedHourIndex, hourlyConditions, convectiveHours]);

  const glideWindDir = useMemo(() => {
    if (selectedHourIndex >= 0 && hourlyConditions[selectedHourIndex]) {
      return hourlyConditions[selectedHourIndex].windDir ?? 0;
    }
    if (convectiveHours.length > 0) {
      return Math.round(convectiveHours.reduce((s, h) => s + (h.windDir ?? 0), 0) / convectiveHours.length);
    }
    return 0;
  }, [selectedHourIndex, hourlyConditions, convectiveHours]);

  // Effective glide radius after safety margin. Cumulus base is already estimated AGL.
  const safetyMargin = settings.safetyMargin;
  const terrainElevation = globalAerodrome.elevation; // metres AMSL
  const ceilingAGL = Math.max(0, glideCircleCeiling);
  const effectiveCeiling = Math.max(0, ceilingAGL - safetyMargin);
  const glideRatio = 20;
  const glideRadiusM = effectiveCeiling * glideRatio; // metres

  // Front data — generated based on active aerodrome position
  const fronts = useMemo(
    () => generateFronts(globalAerodrome.lat, globalAerodrome.lon, hourlyConditions.length || 12),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [globalAerodrome.icao, hourlyConditions.length]
  );

  const handleSetActiveAerodrome = useCallback(
    (ad: RegionalAerodrome) => {
      const mockMatch = MOCK_AERODROMES.find((m) => m.icao === ad.icao);
      if (mockMatch) {
        setSelectedAerodrome(mockMatch);
      } else {
        setSelectedAerodrome({
          icao: ad.icao,
          name: ad.name,
          lat: ad.lat,
          lon: ad.lon,
          elevation: Math.round(ad.elevation * 0.3048),
          country: ad.lon < -30 ? 'CA' : 'FR',
        });
      }
    },
    [setSelectedAerodrome]
  );

  return (
    <div className="min-h-screen px-3 sm:px-4 md:px-6 xl:px-8 2xl:px-10 py-4 sm:py-6 max-w-screen-2xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
            Carte Météorologique
          </h1>
          <p className="text-xs sm:text-sm mt-0.5" style={{ color: 'var(--text-muted)' }}>
            {globalAerodrome.icao} · {globalAerodrome.name} · Rayon {radius} km · OpenStreetMap + Open-Meteo
          </p>
        </div>

        {/* Radius slider */}
        <div className="flex items-center gap-4">
          <div
            className="flex items-center gap-3 px-3 sm:px-4 py-2 rounded-xl w-full sm:w-auto"
            style={{ backgroundColor: 'var(--surface)', border: '1px solid var(--border)' }}
          >
            <span className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>Rayon</span>
            <input
              type="range"
              min={10}
              max={300}
              step={10}
              value={radius}
              onChange={(e) => setRadius(parseInt(e.target.value))}
              className="flex-1 sm:w-28 touch-manipulation"
              style={{ accentColor: '#00d4ff' }}
            />
            <span className="text-sm font-semibold w-16 font-mono" style={{ color: '#00d4ff' }}>
              {radius} km
            </span>
          </div>
        </div>
      </div>

      {/* Glide circle timeline selector */}
      {hourlyConditions.length > 0 && (
        <div
          className="mb-4 px-3 sm:px-4 py-3 rounded-xl"
          style={{ backgroundColor: 'var(--surface)', border: '1px solid var(--border)' }}
        >
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <Clock size={14} style={{ color: '#f97316' }} />
            <span className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>
              Cercle Finesse {glideRatio}
            </span>
            <span className="text-xs hidden sm:inline" style={{ color: 'var(--text-muted)' }}>
              — Rayon = (Plafond AGL − Marge {safetyMargin}m) × {glideRatio}
            </span>
          </div>
          {/* Timeline buttons — horizontally scrollable on mobile */}
          <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1">
            <button
              onClick={() => setSelectedHourIndex(-1)}
              className="px-3 py-2 rounded-md text-xs font-semibold transition-all touch-manipulation flex-shrink-0"
              style={{
                backgroundColor: selectedHourIndex === -1 ? 'rgba(249,115,22,0.2)' : 'rgba(255,255,255,0.04)',
                border: `1px solid ${selectedHourIndex === -1 ? '#f97316' : 'var(--border)'}`,
                color: selectedHourIndex === -1 ? '#f97316' : 'var(--text-muted)',
              }}
            >
              Max
            </button>
            {hourlyConditions.map((h, idx) => {
              const hourLabel = h.hour ?? `${String(idx + 6).padStart(2, '0')}h`;
              const isConv = h.isConvective;
              const isSelected = selectedHourIndex === idx;
              return (
                <button
                  key={idx}
                  onClick={() => setSelectedHourIndex(idx)}
                  className="px-2.5 py-2 rounded-md text-xs font-mono transition-all touch-manipulation flex-shrink-0"
                  style={{
                    backgroundColor: isSelected ? 'rgba(249,115,22,0.2)' : isConv ? 'rgba(249,115,22,0.06)' : 'rgba(255,255,255,0.02)',
                    border: `1px solid ${isSelected ? '#f97316' : isConv ? 'rgba(249,115,22,0.3)' : 'var(--border)'}`,
                    color: isSelected ? '#f97316' : isConv ? '#fbbf24' : 'var(--text-muted)',
                  }}
                  title={`${hourLabel} — Plafond: ${h.cumulusBase ?? 0}m — Rayon: ${Math.round(Math.max(0, ((h.cumulusBase ?? 0) - terrainElevation - safetyMargin)) * glideRatio / 1000)}km`}
                >
                  {hourLabel}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-3 mt-2 flex-wrap">
            <div className="flex items-center gap-2">
              <div className="w-3 h-0.5 rounded" style={{ backgroundColor: '#f97316', borderTop: '1px dashed #f97316' }} />
              <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                Plafond:{' '}
                <span style={{ color: '#f97316', fontWeight: 600 }}>
                  {glideCircleCeiling > 0
                    ? `${ceilingAGL}m AGL → rayon ${Math.round(glideRadiusM / 1000)}km`
                    : 'Données insuffisantes'}
                </span>
              </span>
            </div>
            {/* Front toggle */}
            <button
              onClick={() => setShowFronts((v) => !v)}
              className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-semibold transition-all touch-manipulation"
              style={{
                backgroundColor: showFronts ? 'rgba(126,200,227,0.1)' : 'rgba(255,255,255,0.03)',
                border: `1px solid ${showFronts ? 'rgba(126,200,227,0.35)' : 'var(--border)'}`,
                color: showFronts ? '#7ec8e3' : 'var(--text-muted)',
              }}
            >
              <span style={{ fontSize: 10 }}>⛅</span>
              Fronts indicatifs
            </button>
            {showFronts && (
              <div className="flex items-center gap-3 flex-wrap">
                <div className="flex items-center gap-1.5">
                  <div className="w-6 h-0.5" style={{ backgroundColor: '#ef4444' }} />
                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: '#ef4444', opacity: 0.7 }} />
                  <span className="text-[10px]" style={{ color: '#ef4444' }}>Front Chaud (indicatif)</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <div className="w-6 h-0.5" style={{ backgroundColor: '#3b82f6' }} />
                  <div style={{ width: 0, height: 0, borderLeft: '4px solid transparent', borderRight: '4px solid transparent', borderBottom: '6px solid #3b82f6' }} />
                  <span className="text-[10px]" style={{ color: '#3b82f6' }}>Front Froid (indicatif)</span>
                </div>
                <span className="text-[10px] italic" style={{ color: 'var(--text-muted)' }}>Synthétiques — non issus d'analyse synoptique</span>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-4 gap-4">
        {/* Map area — full height on mobile */}
        <div
          className="xl:col-span-3 rounded-xl overflow-hidden relative"
          style={{
            backgroundColor: '#060d1a',
            border: '1px solid var(--border)',
            height: '55vw',
            minHeight: '300px',
            maxHeight: '600px',
          }}
        >
          {/* Overlay controls — smaller on mobile */}
          <div className="absolute top-3 left-3 z-[1000] flex flex-col gap-1.5">
            {[
              { key: 'cbh', label: 'Base Cu', icon: <Cloud size={14} /> },
              { key: 'radar', label: 'Radar pluie', icon: <CloudRain size={14} /> },
              { key: 'pressure', label: 'Pression', icon: <Gauge size={14} /> },
              { key: 'wind', label: 'Vent', icon: <Wind size={14} /> },
              { key: 'storm', label: 'Orages', icon: <CloudLightning size={14} /> },
            ].map((ov) => (
              <button
                key={`overlay-${ov.key}`}
                onClick={() => setActiveOverlay(ov.key as 'cbh' | 'wind' | 'storm' | 'radar' | 'pressure')}
                className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-2 rounded-lg text-xs font-medium transition-all duration-150 active:scale-95 touch-manipulation"
                style={{
                  backgroundColor:
                    activeOverlay === ov.key ? 'color-mix(in srgb, var(--accent-cyan) 14%, var(--card))' : 'var(--card)',
                  border: `1px solid ${activeOverlay === ov.key ? 'rgba(0,212,255,0.4)' : 'var(--border)'}`,
                  color: activeOverlay === ov.key ? '#00d4ff' : 'var(--text-muted)',
                  backdropFilter: 'blur(8px)',
                }}
              >
                {ov.icon}
                <span className="hidden sm:inline">{ov.label}</span>
              </button>
            ))}
          </div>

          {activeOverlay === 'radar' && (
            <div className="absolute top-3 left-[8.75rem] right-3 z-[1000] flex flex-wrap gap-1.5">
              {Object.values(RADAR_LAYERS).map((layer) => (
                <button
                  key={`radar-layer-${layer.key}`}
                  onClick={() => {
                    setActiveRadarLayer(layer.key);
                    setOverlayPlaying(false);
                  }}
                  className="px-2.5 py-2 rounded-lg text-xs font-medium transition-all duration-150 active:scale-95 touch-manipulation"
                  style={{
                    backgroundColor:
                      activeRadarLayer === layer.key ? 'color-mix(in srgb, #00d4ff 16%, var(--card))' : 'var(--card)',
                    border: `1px solid ${activeRadarLayer === layer.key ? 'rgba(0,212,255,0.5)' : 'var(--border)'}`,
                    color: activeRadarLayer === layer.key ? '#00d4ff' : 'var(--text-muted)',
                    backdropFilter: 'blur(8px)',
                  }}
                >
                  {layer.label}
                </button>
              ))}
            </div>
          )}

          {/* Loading overlay */}
          {(globalLoading || overlayLoading) && (
            <div
              className="absolute bottom-4 left-1/2 -translate-x-1/2 z-[1000] flex items-center gap-2 px-3 py-2 rounded-lg text-xs"
              style={{
                backgroundColor: 'var(--card)',
                border: '1px solid var(--border)',
                color: 'var(--text-muted)',
                backdropFilter: 'blur(8px)',
              }}
            >
              <Loader2 size={13} className="animate-spin" style={{ color: '#00d4ff' }} />
              {overlayLoading ? 'Chargement couche météo…' : 'Chargement terrains…'}
            </div>
          )}

          {/* Animated weather overlay timeline */}
          {animatedOverlayActive && overlayHours.length > 0 && (
            <div
              className="absolute left-3 right-3 bottom-4 z-[1000] flex flex-col sm:flex-row sm:items-center gap-2 px-3 py-2 rounded-lg"
              style={{
                backgroundColor: 'var(--card)',
                border: '1px solid var(--border)',
                backdropFilter: 'blur(8px)',
              }}
            >
              <button
                onClick={() => setOverlayPlaying((v) => !v)}
                className="flex items-center justify-center w-8 h-8 rounded-md transition-all active:scale-95"
                style={{ backgroundColor: 'rgba(0,212,255,0.12)', color: '#00d4ff', border: '1px solid rgba(0,212,255,0.35)' }}
                title={overlayPlaying ? 'Pause' : 'Animer'}
              >
                {overlayPlaying ? <Pause size={14} /> : <Play size={14} />}
              </button>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>
                    {activeOverlay === 'radar' ? activeRadarConfig.label : activeOverlay === 'storm' ? 'Orages' : activeOverlay === 'wind' ? 'Vent' : 'Pression'}
                  </span>
                  <span className="text-xs font-mono" style={{ color: '#00d4ff' }}>{activeOverlayHour}</span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={Math.max(0, overlayHours.length - 1)}
                  value={Math.min(overlayHourIndex, Math.max(0, overlayHours.length - 1))}
                  onChange={(e) => setOverlayHourIndex(Number(e.target.value))}
                  className="w-full"
                  style={{ accentColor: '#00d4ff' }}
                />
              </div>
            </div>
          )}

          {activeOverlay === 'radar' && radarStatus && (
            <div
              className="absolute right-3 top-16 z-[1000] max-w-[330px] px-3 py-2 rounded-lg text-xs leading-snug"
              style={{
                backgroundColor: 'var(--card)',
                border: `1px solid ${radarStatus.includes('indisponible') ? 'rgba(248,113,113,0.45)' : 'rgba(0,212,255,0.35)'}`,
                color: radarStatus.includes('indisponible') ? '#fca5a5' : 'var(--text-primary)',
                backdropFilter: 'blur(8px)',
              }}
            >
              {radarStatus}
            </div>
          )}

          {/* Timestamp badge */}
          {!animatedOverlayActive && (
          <div
            className="absolute bottom-4 left-4 z-[1000] px-3 py-1.5 rounded-lg text-xs font-mono pointer-events-none hidden sm:block"
            style={{
              backgroundColor: 'var(--card)',
              border: '1px solid var(--border)',
              color: 'var(--text-muted)',
              backdropFilter: 'blur(8px)',
            }}
          >
            OpenStreetMap · Open-Meteo · {dateStr}
          </div>
          )}

          {/* Formula reminder card — bottom-right corner of map */}
          {glideRadiusM > 0 && !animatedOverlayActive && (
            <div
              className="absolute bottom-4 right-4 z-[1000] pointer-events-none"
              style={{
                backgroundColor: 'var(--card)',
                border: '1px solid rgba(255,107,0,0.55)',
                borderRadius: '8px',
                padding: '6px 10px',
                backdropFilter: 'blur(8px)',
                maxWidth: '210px',
              }}
            >
              <div className="flex items-center gap-1.5 mb-1">
                <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#ff6b00', boxShadow: '0 0 6px #ff6b00' }} />
                <span className="text-xs font-bold" style={{ color: '#ff6b00', fontFamily: 'IBM Plex Mono, monospace' }}>
                  Zone de Portée
                </span>
              </div>
              <p className="text-xs leading-snug" style={{ color: 'var(--text-secondary)', fontFamily: 'IBM Plex Mono, monospace', fontSize: '10px' }}>
                Portée basée sur :<br />
                Finesse {glideRatio} / Marge sécu : {safetyMargin}m
              </p>
              {glideWindSpeed > 10 && (
                <p className="mt-1 text-xs" style={{ color: '#fbbf24', fontSize: '9px', fontFamily: 'IBM Plex Mono, monospace' }}>
                  ⚠ Déformée — Vent {Math.round(glideWindSpeed)} km/h
                </p>
              )}
            </div>
          )}

          {/* Leaflet Map */}
          <LeafletMap
            selectedAerodrome={globalAerodrome}
            regionalAerodromes={regionalAerodromes}
            weatherData={weatherData}
            weatherOverlay={weatherOverlay}
            radarHost={radarHost}
            radarFrames={radarFrames}
            activeRadarLayer={activeRadarLayer}
            onRadarStatus={setRadarStatus}
            activeOverlay={activeOverlay}
            overlayHourIndex={overlayHourIndex}
            overlayHourLabel={activeOverlayHour}
            radius={radius}
            onSetActiveAerodrome={handleSetActiveAerodrome}
            ceilingAltitude={glideRadiusM}
            windSpeed={glideWindSpeed}
            windDir={glideWindDir}
            fronts={showFronts ? fronts : []}
            selectedHourIndex={selectedHourIndex}
            totalHours={hourlyConditions.length || 12}
            safetyMargin={safetyMargin}
            glideRatio={glideRatio}
          />
        </div>

        {/* Right panel — stacked below map on mobile */}
        <div className="flex flex-col gap-4">
          {/* Legend */}
          <MapLegend activeOverlay={activeOverlay} activeRadarLayer={activeRadarLayer} />

          {/* Global aerodrome info card */}
          <div
            className="rounded-xl p-4"
            style={{ backgroundColor: 'var(--surface)', border: '1px solid rgba(0,212,255,0.2)' }}
          >
            <div className="flex items-center gap-2 mb-3">
              <Crosshair size={14} style={{ color: '#00d4ff' }} />
              <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                Terrain sélectionné
              </h3>
            </div>
            <div className="space-y-1.5">
              <p className="font-mono text-sm font-bold" style={{ color: '#00d4ff' }}>
                {globalAerodrome.icao}
              </p>
              <p className="text-xs" style={{ color: 'var(--text-primary)' }}>
                {globalAerodrome.name}
              </p>
              <p className="text-xs font-mono" style={{ color: 'var(--text-muted)' }}>
                {globalAerodrome.lat.toFixed(3)}°N · {globalAerodrome.lon.toFixed(3)}°
              </p>
              <p className="text-xs font-mono" style={{ color: 'var(--text-muted)' }}>
                Élév. {globalAerodrome.elevation}m AMSL
              </p>
              {glideCircleCeiling > 0 && (
                <div className="mt-2 pt-2" style={{ borderTop: '1px solid var(--border)' }}>
                  <p className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: '#f97316' }}>
                    Cercle Finesse {glideRatio}
                  </p>
                  <p className="text-xs font-mono" style={{ color: '#fbbf24' }}>
                    Plafond AGL: {ceilingAGL}m
                  </p>
                  <p className="text-xs font-mono" style={{ color: 'var(--text-muted)' }}>
                    − Marge: {safetyMargin}m
                  </p>
                  <p className="text-xs font-mono" style={{ color: '#f97316' }}>
                    Rayon: ~{Math.round(glideRadiusM / 1000)}km
                  </p>
                  {glideWindSpeed > 10 && (
                    <p className="text-[10px] font-mono mt-1" style={{ color: '#a78bfa' }}>
                      Vent {Math.round(glideWindSpeed)} km/h → patatoïde
                    </p>
                  )}
                </div>
              )}
              {showFronts && fronts.length > 0 && (
                <div className="mt-2 pt-2" style={{ borderTop: '1px solid var(--border)' }}>
                  <p className="text-[10px] font-semibold uppercase tracking-wider mb-1.5" style={{ color: '#7ec8e3' }}>
                    Fronts indicatifs (synthétiques)
                  </p>
                  <p className="text-[9px] italic mb-1.5" style={{ color: 'var(--text-muted)' }}>
                    Non issus d'une analyse synoptique réelle
                  </p>
                  {fronts.map((f, i) => {
                    const impactHour = hourlyConditions[f.impactHourIndex]?.hour ?? `H+${f.impactHourIndex}`;
                    return (
                      <div key={i} className="flex items-center justify-between mb-1">
                        <span className="text-[10px]" style={{ color: f.type === 'warm' ? '#ef4444' : '#3b82f6' }}>
                          {f.label} (indicatif)
                        </span>
                        <span className="text-[10px] font-mono" style={{ color: 'var(--text-muted)' }}>
                          ~{impactHour}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Area summary */}
          <div
            className="rounded-xl p-4"
            style={{ backgroundColor: 'var(--surface)', border: '1px solid var(--border)' }}
          >
            <div className="flex items-center gap-2 mb-3">
              <Layers size={14} style={{ color: '#00d4ff' }} />
              <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                Résumé Zone {radius}km
              </h3>
            </div>
            {globalLoading && fetchedAerodromes.length === 0 ? (
              <div className="flex items-center gap-2 py-2">
                <Loader2 size={14} className="animate-spin" style={{ color: '#00d4ff' }} />
                <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Chargement…</span>
              </div>
            ) : (
              <div className="space-y-2">
                {[
                  {
                    label: 'Terrains favorables',
                    value: `${favorableCount}/${fetchedAerodromes.length}`,
                    color: '#34d399',
                  },
                  {
                    label: 'CBH moyenne zone',
                    value: avgCBH > 0 ? `${avgCBH}m` : '--',
                    color: '#a78bfa',
                  },
                  {
                    label: 'Terrains orageux',
                    value: `${stormCount} détecté${stormCount > 1 ? 's' : ''}`,
                    color: '#a78bfa',
                  },
                ].map((item) => (
                  <div key={`area-${item.label}`} className="flex items-center justify-between">
                    <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                      {item.label}
                    </span>
                    <span className="font-mono text-xs font-semibold" style={{ color: item.color }}>
                      {item.value}
                    </span>
                  </div>
                ))}
                {globalLoading && (
                  <div className="flex items-center gap-1.5 pt-1">
                    <Loader2 size={11} className="animate-spin" style={{ color: 'var(--text-muted)' }} />
                    <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                      Mise à jour en cours…
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Info note */}
          <div
            className="flex items-start gap-2 px-3 py-3 rounded-xl text-xs"
            style={{
              backgroundColor: 'rgba(0,212,255,0.04)',
              border: '1px solid rgba(0,212,255,0.1)',
            }}
          >
            <Info size={13} className="flex-shrink-0 mt-0.5" style={{ color: '#00d4ff' }} />
            <p style={{ color: 'var(--text-muted)' }}>
              Cliquez sur un marqueur OACI pour voir les détails et définir le terrain actif. La couche
              météo (pluie/vent) est superposée via Open-Meteo en temps réel.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
